import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Api from '../../../../../constants/Api';
import axiosInstance from '../../../../../services/AxiosInstance';
import { validateEmail } from '../../../../../utils/CheckUtils';
import { formatDateToYYYYMMDD, parseToUTCDate } from '../../../../../utils/DateUtils';
import { formatCPF } from '../../../../../utils/FormatUtils';
import { resolveSingleParameter } from '../../../../../utils/ParameterUtils';
import { errorObjectAlert } from '../../../alert/defaultAlert';
import CpfInput from '../../../Common/CpfInput';
import InputDate from '../../../Forms/Element/InputDate';
import InputText from '../../../Forms/Element/InputText';
import BranchSelect from '../Branch/BranchSelect';
import './CustomerQuickCreateModal.css';

// Espera entre a última tecla e a busca por telefone repetido, para não consultar a cada
// dígito enquanto o operador ainda está digitando o número.
const DUPLICATE_LOOKUP_DEBOUNCE_MS = 450;

// Abaixo disso o número ainda está incompleto e a busca traria gente demais.
const MIN_DIGITS_FOR_DUPLICATE_LOOKUP = 8;

// Ano mínimo aceito no nascimento. A máscara deixa digitar qualquer ano de quatro dígitos,
// e um "0199" no lugar de "1999" seria gravado como o ano 199 sem que ninguém percebesse.
const MIN_BIRTH_YEAR = 1900;

/** Deixa só os dígitos: o operador digita com parênteses, espaço e traço. */
const onlyDigits = (value) => (value || '').replace(/\D/g, '');

/** Nome que o cliente terá depois de gravado, do mesmo jeito que o servidor monta. */
const buildFullName = (firstName, lastName) => [firstName, lastName]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

/**
 * Cadastro de cliente sem sair do fluxo em que o operador está.
 *
 * Pede o mínimo para marcar um horário. Os campos que a empresa tornou obrigatórios no
 * cadastro completo (filial e CPF, em Configurações > Cadastros) continuam obrigatórios
 * aqui: esta tela é um atalho, não uma exceção à regra do tenant.
 *
 * Com customerId, o mesmo formulário atualiza um cliente que já existe (usado no detalhe do
 * agendamento): carrega o cadastro, grava pelo PATCH e devolve o cliente em onUpdated.
 * Foto, gênero, situação e endereços continuam só no cadastro completo.
 */
const CustomerQuickCreateModal = ({ show, onHide, onCreated, onUpdated, customerId, defaultBranch, onExited }) => {
    const isEdit = Boolean(customerId);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [document, setDocument] = useState('');
    const [email, setEmail] = useState('');
    const [birthDate, setBirthDate] = useState(null);
    const [branch, setBranch] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Cadastro gravado, na edição. Guarda o que o formulário não mostra (gênero) para a
    // gravação devolver ao servidor, e o telefone original para a busca de repetidos.
    const [loadedCustomer, setLoadedCustomer] = useState(null);
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    // Parâmetros da empresa: null enquanto não foram lidos, para o formulário não julgar
    // um campo obrigatório antes de saber a configuração.
    const [requireBranch, setRequireBranch] = useState(null);
    const [requireCpf, setRequireCpf] = useState(null);

    const [duplicates, setDuplicates] = useState([]);
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);
    const duplicateRequestRef = useRef(0);

    const parametersLoaded = requireBranch !== null && requireCpf !== null;

    /** Volta o formulário ao estado inicial, com a filial do contexto já escolhida. */
    const resetForm = useCallback(() => {
        setFirstName('');
        setLastName('');
        setPhone('');
        setDocument('');
        setEmail('');
        setBirthDate(null);
        setBranch(defaultBranch || null);
        setErrors({});
        setDuplicates([]);
        setCheckingDuplicates(false);
        setLoadedCustomer(null);
        setLoadFailed(false);
    }, [defaultBranch]);

    useEffect(() => {
        if (show) {
            resetForm();
        }
    }, [show, resetForm]);

    /**
     * Na edição, carrega o cadastro a cada abertura em vez de usar o cliente que veio do
     * agendamento: aquele é uma cópia de quando a agenda carregou. Se alguém alterou o
     * cliente em outra tela nesse meio tempo, gravar a partir da cópia desfaria a alteração.
     */
    useEffect(() => {
        if (!show || !customerId) {
            return;
        }

        setLoadingCustomer(true);
        axiosInstance.get(`${Api.main.customer}/${customerId}`)
            .then((response) => {
                const customer = response.data;
                setLoadedCustomer(customer);
                setFirstName(customer?.firstName || '');
                setLastName(customer?.lastName || '');
                // Telefone como está gravado: o formatPhoneNumber embaralha número de 10 dígitos
                // (sem o nono dígito, comum na base) e o operador estranharia o campo.
                setPhone(customer?.phone || '');
                setDocument(formatCPF(customer?.document));
                setEmail(customer?.email || '');
                setBirthDate(customer?.birthDate ? parseToUTCDate(customer.birthDate) : null);
                setBranch(customer?.branch?.id ? { value: customer.branch.id, label: customer.branch.name } : null);
            })
            .catch((error) => {
                setLoadFailed(true);
                errorObjectAlert(error);
            })
            .finally(() => setLoadingCustomer(false));
    }, [show, customerId]);

    // Os parâmetros são lidos a cada abertura: o operador pode tê-los alterado em outra aba.
    useEffect(() => {
        if (!show) {
            return;
        }

        axiosInstance.get(Api.parameter.main)
            .then((response) => {
                const parameter = resolveSingleParameter(response.data);
                setRequireBranch(Boolean(parameter?.requireBranchInCustomer));
                setRequireCpf(Boolean(parameter?.requireCpfInCustomer));
            })
            .catch(() => {
                // Sem os parâmetros, exigir o mínimo é preferível a travar o cadastro:
                // o servidor continua validando o que for realmente obrigatório.
                setRequireBranch(false);
                setRequireCpf(false);
            });
    }, [show]);

    /**
     * Procura clientes com o mesmo telefone.
     *
     * O resultado é só um aviso. Repetir telefone é legítimo e comum: pai e filho, marido e
     * mulher, o celular da casa. Bloquear impediria o cadastro de quem realmente existe;
     * o que se quer evitar é o operador não achar o cliente na busca e criar um segundo
     * registro da mesma pessoa, partindo o histórico em dois.
     */
    const originalPhoneDigits = onlyDigits(loadedCustomer?.phone);

    useEffect(() => {
        const digits = onlyDigits(phone);

        // Na edição, o telefone que o cliente já tinha não é novidade: só vale procurar
        // repetidos quando o operador troca o número.
        if (!show || digits.length < MIN_DIGITS_FOR_DUPLICATE_LOOKUP
            || (isEdit && digits === originalPhoneDigits)) {
            setDuplicates([]);
            setCheckingDuplicates(false);
            return undefined;
        }

        const requestId = duplicateRequestRef.current + 1;
        duplicateRequestRef.current = requestId;
        setCheckingDuplicates(true);

        const timer = setTimeout(() => {
            axiosInstance.post(`${Api.main.customer}/pageable/all-branches`, {
                filters: [{ field: 'phone', value: digits, operation: 'MATCH' }],
                page: 0,
                size: 5,
                orders: [{ field: 'firstName', direction: 'ASC' }]
            })
                .then((response) => {
                    // Descarta respostas de digitações anteriores que chegaram fora de ordem.
                    if (duplicateRequestRef.current !== requestId) {
                        return;
                    }
                    // O próprio cliente em edição não é repetido dele mesmo.
                    setDuplicates((response.data?.content || []).filter((customer) => customer.id !== customerId));
                })
                .catch(() => {
                    if (duplicateRequestRef.current === requestId) {
                        setDuplicates([]);
                    }
                })
                .finally(() => {
                    if (duplicateRequestRef.current === requestId) {
                        setCheckingDuplicates(false);
                    }
                });
        }, DUPLICATE_LOOKUP_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [phone, show, isEdit, originalPhoneDigits, customerId]);

    /** Monta a opção no formato que o CustomerSelect usa, para o cliente já entrar escolhido. */
    const toSelectOption = (customer) => {
        const name = customer.fullName || buildFullName(customer.firstName, customer.lastName);
        const info = customer.phone || customer.document || '';

        return {
            value: customer.id,
            label: info ? `${name} (${info})` : name,
            customer
        };
    };

    const validate = () => {
        const found = {};

        if (!firstName.trim()) {
            found.firstName = 'Informe o nome do cliente.';
        }

        // Telefone é opcional no cadastro completo, mas um cliente de agenda sem telefone
        // não recebe confirmação nem lembrete de WhatsApp.
        if (onlyDigits(phone).length < MIN_DIGITS_FOR_DUPLICATE_LOOKUP) {
            found.phone = 'Informe um telefone válido com DDD.';
        }

        if (requireBranch && !branch) {
            found.branch = 'A filial é obrigatória na configuração da empresa.';
        }

        const documentDigits = onlyDigits(document);

        if (requireCpf && documentDigits.length !== 11) {
            found.document = 'O CPF é obrigatório na configuração da empresa.';
        } else if (documentDigits.length > 0 && documentDigits.length !== 11) {
            // Sem isso um CPF pela metade seria gravado e só apareceria como problema no
            // dia da assinatura, quando o provedor recusasse a cobrança.
            found.document = 'Informe o CPF completo ou deixe o campo em branco.';
        }

        // E-mail é opcional, mas um endereço digitado errado só seria descoberto quando a
        // cobrança da assinatura voltasse sem chegar ao cliente.
        if (email.trim() && !validateEmail(email.trim())) {
            found.email = 'Informe um e-mail válido ou deixe o campo em branco.';
        }

        // Nascimento é opcional, como no cadastro completo. Quando informado, precisa ser uma
        // data possível: é ela que coloca o cliente na lista de aniversariantes.
        if (birthDate && (birthDate > new Date() || birthDate.getFullYear() < MIN_BIRTH_YEAR)) {
            found.birthDate = 'Informe uma data de nascimento válida.';
        }

        setErrors(found);
        return Object.keys(found).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }

        // Na edição, telefone que não mudou volta como estava gravado, mesmo com máscara ou
        // +55: só os dígitos seriam o mesmo número, mas a gravação não deve reformatar o que
        // o operador nem tocou.
        const phoneUnchanged = isEdit && onlyDigits(phone) === originalPhoneDigits;

        const payload = {
            firstName: firstName.trim(),
            lastName: lastName.trim() || null,
            phone: phoneUnchanged ? loadedCustomer.phone : onlyDigits(phone),
            document: onlyDigits(document) || null,
            email: email.trim() || null,
            // Mesmo formato do cadastro completo (aaaa-mm-dd), sem fuso, para o dia não andar.
            birthDate: birthDate ? formatDateToYYYYMMDD(birthDate) : null,
            branch: branch ? { id: branch.value } : null
        };

        setSaving(true);

        if (isEdit) {
            // O PATCH do cliente sobrescreve o gênero com o que vier, inclusive vazio. Ele não
            // aparece aqui, então volta o valor gravado. A situação fica de fora: sem ela o
            // servidor mantém a atual. A foto não passa por esta rota.
            axiosInstance.patch(`${Api.main.customer}/${customerId}`, { ...payload, gender: loadedCustomer?.gender ?? null })
                .then(() => {
                    onUpdated?.({
                        ...loadedCustomer,
                        ...payload,
                        fullName: buildFullName(payload.firstName, payload.lastName),
                        branch: branch ? { id: branch.value, name: branch.label } : null
                    });
                    onHide();
                })
                .catch((error) => errorObjectAlert(error))
                .finally(() => setSaving(false));
            return;
        }

        axiosInstance.post(Api.main.customer, payload)
            .then((response) => {
                onCreated(toSelectOption(response.data));
                onHide();
            })
            .catch((error) => errorObjectAlert(error))
            .finally(() => setSaving(false));
    };

    /** Aproveita um cliente que já existe em vez de criar outro igual. */
    const handleUseExisting = (customer) => {
        onCreated(toSelectOption(customer));
        onHide();
    };

    const previewName = useMemo(() => buildFullName(firstName, lastName), [firstName, lastName]);

    // Campos travados enquanto grava ou enquanto o cadastro da edição ainda não chegou, para
    // ninguém digitar algo que seria sobrescrito pelo carregamento.
    const fieldsDisabled = saving || loadingCustomer || loadFailed;

    /** Texto do botão principal: o nome entra só no cadastro, para confirmar quem será criado. */
    const saveLabel = () => {
        if (saving) {
            return 'Salvando...';
        }
        if (isEdit) {
            return 'Salvar alterações';
        }
        return `Cadastrar${previewName ? ` ${previewName}` : ''}`;
    };

    // onExited avisa quando o modal terminou de sair da tela, já sem o fundo escuro: quem
    // escondeu outro painel para abrir este só deve trazê-lo de volta nesse momento.
    return (
        <Modal show={show} onHide={onHide} onExited={onExited} centered className="customer-quick-create">
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? 'Atualizar cliente' : 'Novo cliente'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="customer-quick-create__intro">
                    {isEdit
                        ? 'Dados principais do cliente. Foto, gênero e endereços ficam em Cadastros > Clientes.'
                        : 'Cadastro reduzido para atender agora. Os demais dados podem ser completados depois em Cadastros > Clientes.'}
                </p>

                {loadingCustomer && (
                    <div className="customer-quick-create__checking mb-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Carregando o cadastro do cliente...
                    </div>
                )}

                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label">Nome <span className="text-danger">*</span></label>
                        <InputText
                            value={firstName}
                            onChange={(event) => setFirstName(event.target.value)}
                            maxLength="30"
                            placeholder="João"
                            error={errors.firstName}
                            disabled={fieldsDisabled}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Sobrenome</label>
                        <InputText
                            value={lastName}
                            onChange={(event) => setLastName(event.target.value)}
                            maxLength="30"
                            placeholder="Silva"
                            disabled={fieldsDisabled}
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Telefone <span className="text-danger">*</span></label>
                        <InputText
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            maxLength="20"
                            placeholder="(11) 99999-9999"
                            error={errors.phone}
                            disabled={fieldsDisabled}
                        />
                    </div>

                    {/* O CPF aparece sempre, mesmo quando a empresa não o exige: sem ele o
                        cliente não consegue assinar o clube depois, porque os provedores de
                        pagamento (Celcoin e Asaas) exigem o documento para criar a
                        cobrança recorrente. Preencher agora evita ter que caçar o cliente
                        de novo no dia da assinatura. */}
                    <div className="col-md-6">
                        <label className="form-label">
                            CPF {requireCpf && <span className="text-danger">*</span>}
                        </label>
                        <CpfInput
                            value={document}
                            onChange={(event) => setDocument(event.target.value)}
                            error={errors.document}
                            disabled={fieldsDisabled}
                        />
                    </div>

                    {/* Mesmo motivo do CPF: a Celcoin também exige o e-mail do cliente para
                        criar a assinatura, e sem ele a venda do clube trava na hora. O limite
                        de 50 caracteres é o tamanho da coluna EMAIL do cliente no banco. */}
                    <div className="col-md-6">
                        <label className="form-label">E-mail</label>
                        <InputText
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            maxLength="50"
                            placeholder="cliente@email.com"
                            error={errors.email}
                            disabled={fieldsDisabled}
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Data de nascimento</label>
                        <InputDate
                            selected={birthDate}
                            onChange={(value) => setBirthDate(value)}
                            maxDate={new Date()}
                            error={errors.birthDate}
                            disabled={fieldsDisabled}
                        />
                    </div>

                    <div className="col-12">
                        <label className="form-label">
                            Filial {requireBranch && <span className="text-danger">*</span>}
                        </label>
                        <BranchSelect
                            value={branch}
                            onChange={(option) => setBranch(option)}
                            error={errors.branch}
                            isDisabled={fieldsDisabled}
                        />
                    </div>
                </div>

                {checkingDuplicates && (
                    <div className="customer-quick-create__checking">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Verificando se esse telefone já está cadastrado...
                    </div>
                )}

                {/* Aviso, nunca bloqueio: o mesmo telefone pode pertencer a mais de um
                    cliente da mesma família. A escolha é do operador. */}
                {!checkingDuplicates && duplicates.length > 0 && (
                    <div className="customer-quick-create__duplicates">
                        <div className="customer-quick-create__duplicates-title">
                            <i className="fas fa-circle-info"></i>
                            Já existe cadastro com esse telefone
                        </div>
                        <p className="customer-quick-create__duplicates-hint">
                            {isEdit
                                ? 'Confira se não é a mesma pessoa cadastrada duas vezes. Se for outra pessoa da família, pode salvar normalmente.'
                                : 'Se for a mesma pessoa, use o cadastro existente para não dividir o histórico. Se for outra pessoa da família, siga com o novo cadastro.'}
                        </p>
                        <ul className="customer-quick-create__duplicates-list">
                            {duplicates.map((customer) => (
                                <li key={customer.id}>
                                    <span>
                                        {customer.fullName || buildFullName(customer.firstName, customer.lastName)}
                                    </span>
                                    {/* Na edição não há o que trocar: o cliente já é este. */}
                                    {!isEdit && (
                                        <button
                                            type="button"
                                            onClick={() => handleUseExisting(customer)}
                                            disabled={saving}
                                        >
                                            Usar este
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={fieldsDisabled || !parametersLoaded}>
                    {saveLabel()}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CustomerQuickCreateModal;
